import { createClient } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

function supabaseUrlFromEnv() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    (process.env.EXPO_PUBLIC_SUPABASE_URL || extra.supabaseUrl || '').trim()
  );
}

/** Project URL only (e.g. building storage public object URLs). */
export function getSupabaseProjectUrl() {
  return supabaseUrlFromEnv();
}

function supabaseAnonKeyFromEnv() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    (process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || extra.supabaseAnonKey || '').trim()
  );
}

let cachedSupabase = null;
let cachedFingerprint = '';

/**
 * Client-side Supabase (publishable / anon key). Provide either:
 * - EXPO_PUBLIC_SUPABASE_URL + EXPO_PUBLIC_SUPABASE_ANON_KEY (webpack/.env), or
 * - expo.extra.supabaseUrl + expo.extra.supabaseAnonKey in app.json (local builds).
 *
 * Optional: EXPO_PUBLIC_SUPABASE_STORY_BUCKET or extra.supabaseStoryBucket (default "storyImages").
 * Optional: EXPO_PUBLIC_SUPABASE_EXERCISE_IMAGE_BUCKET or extra.supabaseExerciseImageBucket (default "exerciseImageBucket").
 * Optional: EXPO_PUBLIC_SUPABASE_PROFILE_BUCKET or extra.supabaseProfileBucket (default "profilePictureBucket").
 * Optional: EXPO_PUBLIC_SUPABASE_GRADE_BUCKET or extra.supabaseGradeBucket (default "gradeImageBucket"; file URL stored in students.grade_url).
 * Optional: EXPO_PUBLIC_SUPABASE_COURSE_IMAGE_BUCKET / extra.supabaseCourseImageBucket (default "courseImagesBucket").
 * Optional: EXPO_PUBLIC_SUPABASE_COURSE_VIDEOS_BUCKET / extra.supabaseCourseVideosBucket (default "courseVideosBucket").
 * Optional: EXPO_PUBLIC_SUPABASE_HANDBOOK_ENTRY_BUCKET / extra.supabaseHandbookEntryBucket (default "handbook_entry_Bucket").
 * Optional: EXPO_PUBLIC_SUPABASE_COMMENT_IMAGES_BUCKET / extra.supabaseCommentImagesBucket (default "comments_images_bucket") for handbook comment images.
 * Optional: EXPO_PUBLIC_SUPABASE_DIM_MAK_MERIDIANS_BUCKET / extra.supabaseDimMakMeridiansBucket (default "dim_mak_meridians").
 * Optional: EXPO_PUBLIC_SUPABASE_TAO_DOC_BUCKET / extra.supabaseTaoDocBucket (default "tao_doc").
 *
 * Uses one shared client per URL+key so the browser does not warn about multiple GoTrue instances.
 */
export function getSupabaseClient() {
  const url = supabaseUrlFromEnv();
  const anonKey = supabaseAnonKeyFromEnv();
  if (!url || !anonKey) return null;
  const fp = `${url}\0${anonKey}`;
  if (!cachedSupabase || cachedFingerprint !== fp) {
    cachedSupabase = createClient(url, anonKey, {
      auth: {
        detectSessionInUrl: Platform.OS === 'web',
        persistSession: true,
        autoRefreshToken: true,
      },
    });
    cachedFingerprint = fp;
  }
  return cachedSupabase;
}

export function getStoryImagesBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_STORY_BUCKET ||
    extra.supabaseStoryBucket ||
    'storyImages'
  ).trim();
}

export function getExerciseImagesBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_EXERCISE_IMAGE_BUCKET ||
    extra.supabaseExerciseImageBucket ||
    'exerciseImageBucket'
  ).trim();
}

/** Supabase Storage bucket for student profile photos (S3-compatible backend). */
export function getProfilePictureBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_PROFILE_BUCKET ||
    extra.supabaseProfileBucket ||
    'profilePictureBucket'
  ).trim();
}

/** Default bucket name `gradeImageBucket`; URL is persisted on `students.grade_url`. */
export function getGradeImageBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_GRADE_BUCKET ||
    extra.supabaseGradeBucket ||
    'gradeImageBucket'
  ).trim();
}

/**
 * Course gallery uploads use **Supabase Storage** (bucket id below). That is S3-backed on Supabase’s
 * infrastructure, not a separate bucket you upload to from the AWS console unless you wired that yourself.
 *
 * Override: EXPO_PUBLIC_SUPABASE_COURSE_IMAGE_BUCKET or expo.extra.supabaseCourseImageBucket.
 */
export function getCourseImageBucketConfigMeta() {
  const extra = Constants?.expoConfig?.extra ?? {};
  const fromEnv = (process.env.EXPO_PUBLIC_SUPABASE_COURSE_IMAGE_BUCKET || '').trim();
  if (fromEnv) {
    return { bucketId: fromEnv, source: 'EXPO_PUBLIC_SUPABASE_COURSE_IMAGE_BUCKET' };
  }
  const fromExtra = (extra.supabaseCourseImageBucket || '').trim();
  if (fromExtra) {
    return { bucketId: fromExtra, source: 'expo.extra.supabaseCourseImageBucket' };
  }
  return { bucketId: 'courseImagesBucket', source: 'default' };
}

export function getCourseImagesBucketName() {
  return getCourseImageBucketConfigMeta().bucketId;
}

export function getCourseVideosBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_COURSE_VIDEOS_BUCKET ||
    extra.supabaseCourseVideosBucket ||
    'courseVideosBucket'
  ).trim();
}

/** Supabase Storage bucket for Dim Mak meridian diagrams. File names match the meridian (e.g. Lung). */
export function getDimMakMeridiansBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_DIM_MAK_MERIDIANS_BUCKET ||
    extra.supabaseDimMakMeridiansBucket ||
    'dim_mak_meridians'
  ).trim();
}

export function getTaoDocBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_TAO_DOC_BUCKET ||
    extra.supabaseTaoDocBucket ||
    'tao_doc'
  ).trim();
}

export function getHandbookEntryBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_HANDBOOK_ENTRY_BUCKET ||
    extra.supabaseHandbookEntryBucket ||
    'handbook_entry_Bucket'
  ).trim();
}

/** Supabase Storage bucket for images attached to comments (e.g. Chi Gung handbook thread). */
export function getCommentImagesBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_COMMENT_IMAGES_BUCKET ||
    extra.supabaseCommentImagesBucket ||
    'comments_images_bucket'
  ).trim();
}

/** Supabase Storage bucket for student profile gallery images. */
export function getStudentGalleryBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_STUDENT_GALLERY_BUCKET ||
    extra.supabaseStudentGalleryBucket ||
    'student_gallery'
  ).trim();
}

/** Supabase Storage bucket for class diary student signature images. */
export function getStudentSignaturesBucketName() {
  const extra = Constants?.expoConfig?.extra ?? {};
  return (
    process.env.EXPO_PUBLIC_SUPABASE_STUDENT_SIGNATURES_BUCKET ||
    extra.supabaseStudentSignaturesBucket ||
    'student_signatures'
  ).trim();
}

export function getSupabaseConfigHint() {
  const missing = [];
  if (!supabaseUrlFromEnv()) {
    missing.push('EXPO_PUBLIC_SUPABASE_URL or expo.extra.supabaseUrl');
  }
  if (!supabaseAnonKeyFromEnv()) {
    missing.push('EXPO_PUBLIC_SUPABASE_ANON_KEY or expo.extra.supabaseAnonKey');
  }
  return missing;
}
